"use client";

import type { ReactNode } from "react";
import { DotsCarousel } from "../../patterns/carousel/dots-carousel";
import { cn } from "../../utils";
import { HeroSection, type HeroSectionProps } from "./hero-section";
import { HERO_SECTION_SLIDER_MAX_SLIDES } from "./hero-section-slider.constants";

type HeroSectionImageMedia = Extract<HeroSectionProps["media"], unknown[]>;

export type HeroSectionSliderMedia =
  | HeroSectionImageMedia
  | {
      src: string;
      /** Required so an inactive video slide has a stable visual preview. */
      poster: string;
      type: "Video";
    };

export type HeroSectionSlide = Omit<
  HeroSectionProps,
  | "contentSize"
  | "media"
  | "mediaActive"
  | "mediaPriority"
  | "showVideoPlaybackControl"
> & {
  id: string;
  media: HeroSectionSliderMedia;
};

export interface HeroSectionSliderProps {
  /** Internal name of the slider entry, reported as the carousel's content_id. */
  internalName?: string;
  /**
   * Complete image or video hero banners. At most the first five are rendered.
   */
  slides: readonly HeroSectionSlide[];
  autoplay?: boolean;
  autoplayDelay?: number;
  ariaLabel?: string;
  goToSlideText?: string;
  slideNavigationText?: string;
  pauseAutoplayText?: string;
  resumeAutoplayText?: string;
  className?: string;
}

const heroDotNavigationClasses = cn(
  "absolute bottom-4 left-4 z-30 mt-0 gap-0 rounded-sm",
  "bg-black/40 px-1 py-0.5 backdrop-blur-[3px]",
  "md:bottom-8 md:left-8"
);

function renderStaticSlide(slide: HeroSectionSlide): ReactNode {
  return (
    <div data-testid="hero-section-slider-slide">
      <HeroSection
        {...slide}
        contentSize="viewport"
        mediaActive
        mediaPriority
        showVideoPlaybackControl
      />
    </div>
  );
}

/**
 * Fullscreen, dots-only carousel for the homepage. It composes complete
 * HeroSection banners, so image and video slides can be mixed freely.
 */
export function HeroSectionSlider(props: Readonly<HeroSectionSliderProps>) {
  const slides = props.slides.slice(0, HERO_SECTION_SLIDER_MAX_SLIDES);

  if (slides.length === 0) {
    return null;
  }

  if (slides.length === 1) {
    return (
      <div className={cn("component-hero-section-slider", props.className)}>
        {renderStaticSlide(slides[0])}
      </div>
    );
  }

  return (
    <DotsCarousel
      ariaLabel={props.ariaLabel ?? "Hero slider"}
      autoplay={props.autoplay}
      autoplayDelay={props.autoplayDelay}
      className={cn("component-hero-section-slider", props.className)}
      dotNavigationClassName={heroDotNavigationClasses}
      goToSlideText={props.goToSlideText}
      pauseAutoplayText={props.pauseAutoplayText}
      renderSlide={(slide, { index, isActive }) => (
        <HeroSection
          {...slide}
          contentSize="viewport"
          mediaActive={isActive}
          mediaPriority={index === 0}
          promotionPosition={index + 1}
          showVideoPlaybackControl
        />
      )}
      resumeAutoplayText={props.resumeAutoplayText}
      slideNavigationText={props.slideNavigationText}
      slides={slides}
      slideTestId="hero-section-slider-slide"
      trackingName={props.internalName}
    />
  );
}
